import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { SectionHead } from "./SectionHead";

// The Steward — the credit economy: the rate card (config with provenance, never constants) and the
// credit ledger (balance derived, never stored). No payments in R1: charging and top-ups are built and
// test-proven but dormant, and refuse rather than appear to work.
export default function TheSteward() {
  const { authedFetch } = useAuth();
  const [tab, setTab] = useState("rate_card");
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    let alive = true;
    authedFetch("/steward/section").then((d) => alive && setV(d)).catch((e) => alive && setErr(String(e)));
    return () => { alive = false; };
  }, [authedFetch]);
  useEffect(load, [load]);

  if (err) return <div className="gk-err" data-testid="steward-error">{err}</div>;
  if (!v) return <div className="loading" data-testid="steward-loading">Loading…</div>;

  const counts = { rate_card: v.rate_card.chargeable.length + v.rate_card.free_actions.length,
                   ledger: v.ledger.accounts.length };

  return (
    <div data-testid="the-steward">
      <SectionHead eyebrow="THE STEWARD · CREDITS AND PAYMENTS" testid="st"
        headline={counts.ledger > 0
          ? `${counts.ledger} member ${counts.ledger === 1 ? "ledger" : "ledgers"} on the books, and no charging in Release 1.`
          : "No member ledgers yet, and no charging in Release 1."}>
        <p className="gk-constraint" data-testid="steward-constraint">{v.constraint}</p>
      </SectionHead>

      <div role="tablist" className="tabs">
        {[["rate_card", "Rate card"], ["ledger", "Ledger"]].map(([id, label]) => (
          <button key={id} role="tab" data-testid={`st-tab-${id}`} aria-selected={tab === id}
                  className="tab" onClick={() => setTab(id)}>
            {label}<span className="tab-count">{counts[id]}</span>
          </button>
        ))}
      </div>

      {tab === "rate_card" && <RateCard rc={v.rate_card} dormancy={v.dormancy} authedFetch={authedFetch} reload={load} />}
      {tab === "ledger" && <Ledger ledger={v.ledger} rules={v.rules} dormancy={v.dormancy} />}
    </div>
  );
}

function Prov({ meta }) {
  if (!meta || !meta.version) return <span className="gk-muted">unset</span>;
  return <span className="gk-muted" title={meta.at || ""}>v{meta.version} · {meta.actor || "—"}</span>;
}

// Inline confirm/correct with a plan preview before it applies (Principle 1): enter a value → Preview
// (what changes, from what to what) → Edit / Cancel / Proceed. On Proceed the version bumps and the
// provenance badge shows the correction. Proves the rate card is a config surface, not constants.
function NumberEditor({ id, current, unit = "", buildEdit, authedFetch, reload }) {
  const [mode, setMode] = useState("view");     // view | editing | preview
  const [val, setVal] = useState(String(current));
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const fmt = (n) => (unit.startsWith("$") ? `$${n}${unit.slice(1)}` : `${n}${unit ? " " + unit : ""}`);

  async function doPreview() {
    setErr("");
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) { setErr("Enter a non-negative number."); return; }
    setBusy(true);
    try {
      const { key, value } = buildEdit(n);
      const r = await authedFetch("/steward/rate-card/preview", { method: "POST", body: { key, value } });
      setDiff({ n, key, value, changes: r.changes });
      setMode("preview");
    } catch (e) { setErr(String(e)); }
    setBusy(false);
  }

  async function doProceed() {
    setBusy(true);
    try {
      await authedFetch("/steward/rate-card", { method: "POST", body: { key: diff.key, value: diff.value } });
      setMode("view");
      reload();
    } catch (e) { setErr(String(e)); }
    setBusy(false);
  }

  if (mode === "view") {
    return (
      <span className="st-edit-view">
        <button className="linkbtn st-edit-link" data-testid={`st-edit-${id}`}
                onClick={() => { setVal(String(current)); setMode("editing"); }}>Edit</button>
      </span>
    );
  }
  if (mode === "editing") {
    return (
      <span className="st-edit-box" data-testid={`st-editing-${id}`}>
        <input className="st-edit-input" data-testid={`st-input-${id}`} value={val} inputMode="numeric"
               onChange={(e) => setVal(e.target.value.replace(/[^\d.]/g, ""))} autoFocus />
        <button className="act" data-testid={`st-preview-${id}`} disabled={busy} onClick={doPreview}>Preview</button>
        <button className="linkbtn" data-testid={`st-cancel-${id}`} onClick={() => { setMode("view"); setErr(""); }}>Cancel</button>
        {err && <span className="error-inline">{err}</span>}
      </span>
    );
  }
  return (
    <span className="st-edit-box st-preview" data-testid={`st-previewing-${id}`}>
      <span className="st-diff" data-testid={`st-diff-${id}`}>{fmt(current)} <strong>→</strong> {fmt(diff.n)}</span>
      {diff.changes
        ? <button className="act act-approve" data-testid={`st-proceed-${id}`} disabled={busy} onClick={doProceed}>Proceed</button>
        : <span className="gk-muted">no change</span>}
      <button className="act" data-testid={`st-back-${id}`} onClick={() => setMode("editing")}>Edit</button>
      <button className="linkbtn" data-testid={`st-cancel2-${id}`} onClick={() => { setMode("view"); setErr(""); }}>Cancel</button>
      {err && <span className="error-inline">{err}</span>}
    </span>
  );
}

function RateCard({ rc, dormancy, authedFetch, reload }) {
  const actionsDict = Object.fromEntries(rc.chargeable.map((a) => [a.action, a.credits]));
  const planName = rc.plan.name || "member";
  return (
    <div className="gk-panel" data-testid="steward-rate-card">
      <p className="gk-note">{rc.note}</p>

      <div className="gk-b9-block">
        <h3>Chargeable actions <Prov meta={rc.chargeable_meta} /></h3>
        <table className="gk-table" data-testid="steward-chargeable-table">
          <thead><tr><th>action</th><th>agent</th><th>credits</th><th></th></tr></thead>
          <tbody>
            {rc.chargeable.map((a) => (
              <tr key={a.action} data-testid={`st-charge-${a.action}`}>
                <td className="mono">{a.action}</td>
                <td>{a.agent}</td>
                <td className="mono">{a.credits}</td>
                <td><NumberEditor id={`action-${a.action}`} current={a.credits} unit="credits"
                      authedFetch={authedFetch} reload={reload}
                      buildEdit={(n) => ({ key: "rate_card.actions", value: { ...actionsDict, [a.action]: n } })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="gk-muted">These agents are Release 2 shells — {dormancy.charging}</p>
      </div>

      <div className="gk-b9-block">
        <h3>Free — always, recorded first-class <Prov meta={rc.free_meta} /></h3>
        <ul className="gk-checks" data-testid="steward-free-list">
          {rc.free_actions.map((f) => (
            <li key={f.action} className="gk-check st-free">
              <span className="gk-check-label">{f.label}</span>
              <span className="gk-check-status">free</span>
            </li>
          ))}
        </ul>
        <p className="gk-muted">Verification and marks are deliberately free — pricing entry would make
          verification a filter not a fact check; pricing a mark would price the trust we give away.</p>
      </div>

      <div className="gk-b9-block">
        <h3>Plan &amp; credits</h3>
        <table className="gk-table" data-testid="steward-plan-table">
          <tbody>
            <tr><td>Monthly plan</td><td className="mono">${rc.plan.price_usd}/mo</td><td><Prov meta={rc.price_meta} /></td>
              <td><NumberEditor id="plan-price" current={rc.plan.price_usd} unit="$/mo" authedFetch={authedFetch} reload={reload}
                    buildEdit={(n) => ({ key: "rate_card.buy_credits_usd", value: n })} /></td></tr>
            <tr><td>Included monthly credits</td><td className="mono">{rc.plan.monthly_credits}</td><td><Prov meta={rc.plan_meta} /></td>
              <td><NumberEditor id="monthly-credits" current={rc.plan.monthly_credits} unit="credits" authedFetch={authedFetch} reload={reload}
                    buildEdit={(n) => ({ key: "plan.default", value: { name: planName, monthly_credits: n } })} /></td></tr>
            <tr><td>Welcome credits at sign-up</td><td className="mono">{rc.welcome.credits}</td><td><Prov meta={rc.welcome_meta} /></td>
              <td><NumberEditor id="welcome" current={rc.welcome.credits} unit="credits" authedFetch={authedFetch} reload={reload}
                    buildEdit={(n) => ({ key: "rate_card.welcome_credits", value: n })} /></td></tr>
            <tr><td>Top-up</td><td className="mono">${rc.top_up.usd_per_credit}/credit</td><td><Prov meta={rc.top_up_meta} /></td>
              <td><NumberEditor id="topup" current={rc.top_up.usd_per_credit} unit="$/credit" authedFetch={authedFetch} reload={reload}
                    buildEdit={(n) => ({ key: "rate_card.top_up_usd", value: n })} /></td></tr>
          </tbody>
        </table>
        <p className="gk-muted">Every number above is configuration, never a constant.</p>
      </div>
    </div>
  );
}

function Ledger({ ledger, rules, dormancy }) {
  return (
    <div className="gk-panel" data-testid="steward-ledger">
      {ledger.fixtures_included && (
        <div className="gk-fixture-banner" data-testid="steward-fixture-banner">
          Showing labelled fixtures — dev review only, never promotable, production never seeded.
        </div>
      )}
      <div className="gk-recorded-note" data-testid="steward-rules">
        <strong>Balance is derived from the ledger, never stored.</strong> Spend drains in order
        <span className="gk-seats"> {rules.spend_order.join(" → ")}</span> — expiring buckets first so a
        paid top-up is not lost before the included allowance. Expiring at renewal:
        <span className="gk-seats"> {rules.expiring_buckets.join(", ")}</span>; persist until spent:
        <span className="gk-seats"> {rules.persisting_buckets.join(", ")}</span>. {rules.renewal}.
      </div>

      {ledger.accounts.length === 0
        ? <div className="empty-state"><p>No credit ledgers. In R1 there are no members yet.</p></div>
        : ledger.accounts.map((a) => (
            <div key={a.account_id} className="held-card" data-testid={`st-account-${a.account_id}`}>
              <div className="held-head">
                <span className="held-kind">member ledger</span>
                <span className="held-field"><strong>{a.name}</strong></span>
                <span className="status-pill sp-unscored" data-testid={`st-balance-${a.account_id}`}>
                  {a.balance.total} credits
                </span>
              </div>
              <table className="gk-table">
                <thead><tr><th>bucket</th><th>kind</th><th>amount</th><th>expires</th><th>reason</th></tr></thead>
                <tbody>
                  {a.rows.map((r, i) => (
                    <tr key={i} className={r.expired ? "gk-warn-text" : ""}>
                      <td className="mono">{r.bucket}</td>
                      <td>{r.kind}</td>
                      <td className="mono">{r.amount}</td>
                      <td>{r.expires_at ? (r.expired ? "expired at renewal" : r.expires_at.slice(0, 10)) : "never"}</td>
                      <td className="reason-cell">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="gk-muted">Derived balance by bucket: {Object.entries(a.balance.buckets)
                .map(([b, n]) => `${b} ${n}`).join(" · ")}</p>
            </div>
          ))}

      <p className="gk-note" data-testid="steward-dormancy">
        <strong>No charging in R1.</strong> {dormancy.charging}. Top-ups: {dormancy.top_ups}.
      </p>
    </div>
  );
}
